import React, { useState, useEffect } from "react";
import {
  HStack,
  Box,
  Stack,
  VStack,
  Text,
  Button,
  Input,
  Radio,
  Divider,
  Pressable,
} from "native-base";
import { TrashIcon } from "../../../assets/icons/icon-assets";
import { Loc } from "../../../App/Loc";
import * as AV from "../../../AVCore/AVCore";
import { Link } from "../../../AVCore/Link";
import { ILink } from "../../../AVCore/Interfaces/Link";
import { IAVEntity } from "../../../AVCore/Interfaces/AVEntity";
import { AVBase } from "../../../AVCore/AVBase";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { setConvo } from "../../../Redux/Actions/convoActions";
import ProfilePic from "../../../components/common/ProfilePicture";
import { useNavigate } from "react-router-dom";

interface Follow {
  id: string;
  title: string;
  type?: string;
  thumb?: string;
}

const Recipients = ({
  removedPeople,
  addedPeople,
  toFK,
  setRemovedPeople,
  setAddedPeople,
  setShowRecipientsPopup,
  setRecipientCount,
  setShowConvoListModal,
  setMdlConvoVisible,
  setMdlCreateConvoVisible,
  setConvoLoading,
  setIsOpenConvo,
}) => {
  const navigate = useNavigate();
  const currentUser = useSelector(
    (state: RootStateOrAny) => state.user.user[0]
  );
  const entitydatalist = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist
  );
  const [searchText, setSearchText] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<Follow[]>([]);
  const [localToFK, setLocalToFK] = React.useState<any[]>(() => {
    // Initialize localToFK with current recipients minus the removed ones plus the added ones
    const currentRecipients = toFK.filter(
      (person) => !removedPeople.some((rp) => rp.id === person.id)
    );
    return [...currentRecipients, ...addedPeople];
  });

  // Search through follows
  React.useEffect(() => {
    if (!currentUser?.follows || !searchText.trim()) {
      setSearchResults([]);
      return;
    }

    const results = currentUser.follows.filter(
      (follow) =>
        follow.type === "Person" &&
        follow.title?.toLowerCase().includes(searchText.toLowerCase()) &&
        !localToFK.some((recipient) => recipient.id === follow.id)
    );

    setSearchResults(results);
  }, [searchText, currentUser?.follows, localToFK]);

  // Update localToFK when addedPeople or removedPeople change
  React.useEffect(() => {
    const currentRecipients = toFK.filter(
      (person) => !removedPeople.some((rp) => rp.id === person.id)
    );
    setLocalToFK([...currentRecipients, ...addedPeople]);
  }, [addedPeople, removedPeople, toFK]);

  const handleSelectFollow = (follow: Follow) => {
    // Remove from removedPeople if it was previously removed
    setRemovedPeople((prev) => prev.filter((p) => p.id !== follow.id));

    // Add to addedPeople if not in original toFK
    if (!toFK.some((p) => p.id === follow.id)) {
      setAddedPeople((prev) => [...prev, follow]);
    }

    setSearchText("");
  };

  const handleRemovePerson = (person) => {
    // If person was in addedPeople, just remove from there
    if (addedPeople.some((p) => p.id === person.id)) {
      setAddedPeople((prev) => prev.filter((p) => p.id !== person.id));
    } else {
      // If person was in original toFK, add to removedPeople
      if (toFK.some((p) => p.id === person.id)) {
        setRemovedPeople((prev) => [...prev, person]);
      }
    }
  };

  const handleBack = () => {
    //setToFK(localToFK);
    setShowRecipientsPopup(false);

    const removedIds = new Set(removedPeople.map((person) => person.id));
    const filteredToFK = toFK.filter((person) => !removedIds.has(person.id));
    const extraAddedPeople = addedPeople.filter(
      (person) => !filteredToFK.some((p) => p.id === person.id)
    );

    const uniquePeople = [...filteredToFK, ...extraAddedPeople];
    setRecipientCount(uniquePeople.length);
  };

  return (
    <VStack h="100%" w="100%" bg="white" space={0}>
      {/* Header */}
      <HStack
        justifyContent="space-between"
        p={4}
        alignItems="center"
        borderBottomWidth={1}
        borderBottomColor="gray.200"
      >
        <Text fontSize="xl" fontWeight="semibold">
          {Loc.currentLang.manageConversation.recipients}
        </Text>
      </HStack>

      {/* Search Input */}
      <Box px={4} py={3} bg="gray.50">
        <Input
          placeholder={Loc.currentLang.manageConversation.searchRecipients}
          value={searchText}
          onChangeText={setSearchText}
          borderRadius="full"
          bg="white"
          borderColor="gray.200"
          _focus={{ borderColor: "#00A7BC" }}
          fontSize="md"
        />
      </Box>

      {/* Search Results and Current Recipients */}
      <Box flex={1} px={4} py={2}>
        <VStack space={3}>
          {/* Search Results */}
          {searchText.trim() &&
            searchResults.map((follow) => (
              <Pressable
                key={follow.id}
                onPress={() => handleSelectFollow(follow)}
                py={2}
              >
                <HStack space={3} alignItems="center">
                  <Box
                    size={10}
                    borderRadius="full"
                    bg="gray.100"
                    overflow="hidden"
                  >
                    <ProfilePic
                      width={38}
                      height={38}
                      link={
                        (entitydatalist?.find(
                          (x: IAVEntity) => x?.id == follow?.id
                        )
                          ? AV.AVEntity.getLink(
                              entitydatalist.find(
                                (x: IAVEntity) => x?.id == follow?.id
                              ) as IAVEntity
                            )
                          : follow) as ILink
                      }
                    />
                  </Box>
                  <Text fontSize="md">{follow.title}</Text>
                </HStack>
              </Pressable>
            ))}

          {/* Current Recipients */}
          {!searchText.trim() &&
            localToFK.map((person) => (
              <HStack
                key={person.id}
                justifyContent="space-between"
                alignItems="center"
                py={2}
              >
                <HStack space={3} alignItems="center">
                  <Box
                    size={10}
                    borderRadius="full"
                    bg="gray.100"
                    overflow="hidden"
                  >
                    <Pressable
                      onPress={() => {
                        setShowConvoListModal(false);
                        setMdlConvoVisible(false);
                        setMdlCreateConvoVisible(false);
                        setConvoLoading(false);
                        setIsOpenConvo(false);
                        handleBack();
                        navigate(`/profile/${person?.id}/${person?.region}`, {
                          state: { user: person },
                        });
                      }}
                    >
                      <ProfilePic
                        width={38}
                        height={38}
                        link={
                          (entitydatalist?.find(
                            (x: IAVEntity) => x?.id == person?.id
                          )
                            ? AV.AVEntity.getLink(
                                entitydatalist.find(
                                  (x: IAVEntity) => x?.id == person?.id
                                ) as IAVEntity
                              )
                            : person) as ILink
                        }
                      />
                    </Pressable>
                  </Box>
                  <Text fontSize="md">{person.title}</Text>
                </HStack>

                {person?.id !== currentUser?.id && (
                  <Pressable
                    onPress={() => handleRemovePerson(person)}
                    hitSlop={8}
                  >
                    <Box p={1}>
                      <TrashIcon height="16" width="16" />
                    </Box>
                  </Pressable>
                )}
              </HStack>
            ))}
        </VStack>
      </Box>

      {/* Footer */}
      <HStack
        p={4}
        borderTopWidth={1}
        borderTopColor="gray.100"
        justifyContent="center"
        bg="gray.50"
      >
        {" "}
        <Button
          rounded="full"
          height="40px"
          width="100px"
          backgroundColor="AVColor.primary"
          onPress={handleBack}
        >
          {Loc.currentLang.manageConversation.back}
        </Button>
      </HStack>
    </VStack>
  );
};
export default Recipients;
